import { ShieldX } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { useEffect, useRef } from 'react';
import { adminSupabase as supabase } from '@/integrations/supabase/adminClient';

interface PermissionDeniedProps {
  route?: string | null;
  requiredPermission?: string | null;
}

export default function PermissionDenied({ route, requiredPermission }: PermissionDeniedProps) {
  const navigate = useNavigate();
  const { adminUser } = useAdminAuth();
  const loggedRef = useRef(false);

  // Log denied access attempt to audit log (fire-and-forget)
  useEffect(() => {
    if (loggedRef.current || !adminUser || !route) return;
    loggedRef.current = true;

    supabase.from('admin_audit_log').insert({
      actor_id: adminUser.userId,
      actor_email: adminUser.email,
      action: 'route_access_denied',
      target_type: 'route',
      target_id: route,
      after_state: {
        required_permission: requiredPermission,
        user_roles: adminUser.roles,
      },
    }).then(() => {});
  }, [adminUser, route, requiredPermission]);

  return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] gap-6 text-center">
      <div className="rounded-full bg-destructive/10 p-6">
        <ShieldX className="h-12 w-12 text-destructive" />
      </div>
      <div className="space-y-2">
        <h1 className="text-2xl font-semibold tracking-tight">Access Denied</h1>
        <p className="text-muted-foreground max-w-md">
          You don't have permission to access this section.
          Contact your administrator to request the required access.
        </p>
      </div>
      {requiredPermission && (
        <code className="text-xs bg-muted px-3 py-1.5 rounded-md font-mono text-muted-foreground">
          Required: {requiredPermission}
        </code>
      )}
      <div className="flex gap-3">
        <Button variant="outline" onClick={() => navigate(-1)}>
          Go Back
        </Button>
        <Button onClick={() => navigate('/admin')}>
          Dashboard
        </Button>
      </div>
    </div>
  );
}
